import React, { useState, useEffect } from "react";
import { Avatar, Stack, Typography } from "@mui/material";
import FilterContainer from "../FilterContainer";
import { IFilterProps } from "../../../../types/filter.type";
import FilterText from "../FilterText";
import FilterList from "../FilterList";
import { ModeType } from "../../../../types/mode.type";
import FilterSelectApi from "../FilterSelectApi";
import FilterRadios from "../FilterRadios";
import { getAvatar } from "../../../../utils/helpers";
import FilterTimeFromTo from "../FilterTimeFromTo";
import moment from "moment";

const modes: { label: string; value: ModeType }[] = [
  {
    label: "Công khai",
    value: "public",
  },
  {
    label: "Bạn bè",
    value: "friends",
  },
  {
    label: "Ẩn danh",
    value: "anonymous",
  },
  {
    label: "Chỉ mình tôi",
    value: "private",
  },
];

const priorities: { label: string; value: string }[] = [
  {
    label: "Tất cả",
    value: "all",
  },
  {
    label: "Thông thường",
    value: "normal",
  },
  {
    label: "Nổi bật",
    value: "popular",
  },
];

function FilterFeed({ setCondition }: IFilterProps) {

  const originFilter: { [key: string]: any } = {
    user: null,
    description: "",
    mode: [],
    priority: "all",
    dateCreatedStart: null,
    dateCreatedEnd: null,
  };
  const [filter, setFilter] = useState(originFilter);

  const handleRefreshFilter = () => {
    setFilter(originFilter);
  };

  useEffect(() => {
    const condition: { [key: string]: any } = {};
    if (filter.description) {
      condition.description = {
        $regex: filter.description?.split(" ")?.join(".*"),
        $options: "i",
      };
    }
    if (filter.mode?.length > 0) {
      condition.mode = { $in: filter.mode };
    }
    if (filter.user) {
      condition.user_created = filter.user.email;
    }
    switch (filter.priority) {
      case "normal":
        condition.isPopular = false;
        break;
      case "popular":
        condition.isPopular = true;
        break;
      default:
        break;
    }
    if (filter.dateCreatedStart || filter.dateCreatedEnd) {
      const startDate = moment(filter.dateCreatedStart)
        .startOf("days")
        .toISOString();
      const endDate = moment(filter.dateCreatedEnd).endOf("days").toISOString();
      if (filter.dateCreatedStart && filter.dateCreatedEnd) {
        condition.date_created = { $gte: startDate, $lte: endDate };
      }
      if (filter.dateCreatedStart && !filter.dateCreatedEnd) {
        condition.date_created = { $gte: startDate };
      }
      if (!filter.dateCreatedStart && filter.dateCreatedEnd) {
        condition.date_created = { $lte: endDate };
      }
    }
    setCondition(condition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <FilterContainer handleRefreshFilter={handleRefreshFilter}>
      <Stack sx={{ width: "100%" }} spacing="10px">
        <FilterText
          title="Nội dung"
          placeholder="Tìm theo nội dung"
          value={filter.description}
          onSearch={(value) => setFilter({ ...filter, description: value })}
        />
        <FilterSelectApi
          direction="column"
          title="Người đăng"
          apiCode="participant"
          placeholder="Chọn người đăng"
          value={filter.user}
          searchFields={["name", "email"]}
          getOptionLabel={(option) => `${option.email} (${option.name})`}
          checkEqual={(value, option) => value?.email === option?.email}
          onChange={(value) => setFilter({ ...filter, user: value })}
          renderOption={(option, { key, isEqual, onClick }) => {
            return (
              <Stack
                key={key}
                direction="row"
                gap={1}
                sx={{
                  px: 0.5,
                  py: 1,
                  mb: 0.5,
                  borderRadius: "10px",
                  backgroundColor: isEqual
                    ? "primary.500"
                    : "background.default",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "primary.500",
                  },
                }}
                onClick={onClick}
              >
                <Avatar
                  src={option?.email ? getAvatar(option?.email, "50") : ""}
                  sx={{ width: "30px", height: "30px" }}
                />
                <Stack>
                  <Typography sx={{ fontWeight: 600 }}>
                    {option?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      lineHeight: "12px",
                    }}
                  >
                    {option?.email}
                  </Typography>
                </Stack>
              </Stack>
            );
          }}
        />
        <FilterTimeFromTo
          title="Ngày đăng"
          defaultTimeFrom={filter.dateCreatedStart}
          defaultTimeTo={filter.dateCreatedEnd}
          onSearch={({ timeFrom, timeTo }) => {
            setFilter({
              ...filter,
              dateCreatedStart: timeFrom,
              dateCreatedEnd: timeTo,
            });
          }}
        />
        <FilterList
          title="Chế độ"
          items={modes}
          defaultValues={filter.mode}
          onChange={(value) => setFilter({ ...filter, mode: value })}
        />
        <FilterRadios
          title="Độ ưu tiên"
          values={priorities}
          defaultValue={filter.priority}
          onChange={(val) => setFilter({ ...filter, priority: val })}
        />
      </Stack>
    </FilterContainer>
  );
}

export default FilterFeed;
